import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, DatePicker, Spin, message } from 'antd';
import { FileTextOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, DollarOutlined } from '@ant-design/icons';
import { LineChart, Line, PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { dashboardService } from '../services/api';

dayjs.locale('pt-br');

const STATUS_COLORS = {
  Pagas: '#52c41a',
  Pendentes: '#faad14',
  'Em atraso': '#ff4d4f',
  Canceladas: '#8c8c8c',
};

const DashboardParcelas = () => {
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [mesAno, setMesAno] = useState(dayjs());

  useEffect(() => {
    carregarDados();
  }, [mesAno]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const params = {
        mes: mesAno.month() + 1,
        ano: mesAno.year(),
      };
      const response = await dashboardService.getParcelasDashboard(params);
      setDados(response.data);
    } catch (error) {
      message.error('Erro ao carregar dashboard de parcelas');
      console.error(error);
    }
    setLoading(false);
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(valor || 0);
  };

  if (loading || !dados) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" tip="Carregando dashboard..." />
      </div>
    );
  }

  const { kpis, distribuicao_status, evolucao_6_meses } = dados;

  return (
    <div className="dashboard-page" style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <h1 style={{ margin: 0 }}>Dashboard Parcelas</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
            Visão operacional de parcelas geradas no ASAAS
          </p>
        </Col>
        <Col>
          <DatePicker
            picker="month"
            value={mesAno}
            onChange={(date) => setMesAno(date || dayjs())}
            format="MMMM/YYYY"
            style={{ width: 200 }}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Parcelas Geradas"
              value={kpis.parcelas_geradas}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Parcelas Pagas"
              value={kpis.parcelas_pagas}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Parcelas Pendentes"
              value={kpis.parcelas_pendentes}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Parcelas em Atraso"
              value={kpis.parcelas_em_atraso}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Parcelas Canceladas"
              value={kpis.parcelas_canceladas}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Valor Devido"
              value={kpis.valor_devido}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Pagos no período: {formatarMoeda(kpis.valor_pago)}
            </p>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Evolução de Parcelas (6 meses)">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={evolucao_6_meses}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="geradas" stroke="#1890ff" strokeWidth={2} name="Geradas" />
                <Line type="monotone" dataKey="pagas" stroke="#52c41a" strokeWidth={2} name="Pagas" />
                <Line type="monotone" dataKey="em_atraso" stroke="#ff4d4f" strokeWidth={2} name="Em atraso" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Distribuição por Status">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={distribuicao_status}
                  dataKey="quantidade"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(entry) => `${entry.status}: ${entry.quantidade}`}
                >
                  {distribuicao_status.map((entry, index) => (
                    <Cell
                      key={`status-cell-${index}`}
                      fill={STATUS_COLORS[entry.status] || '#1890ff'}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Status Mensal de Parcelas">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={evolucao_6_meses}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="pendentes" stackId="a" fill="#faad14" name="Pendentes" />
                <Bar dataKey="em_atraso" stackId="a" fill="#ff4d4f" name="Em atraso" />
                <Bar dataKey="canceladas" stackId="a" fill="#8c8c8c" name="Canceladas" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardParcelas;
